/**
 * Size markers for inline images. Encoded as an alt-text suffix in the wire markdown form so the
 * size choice round-trips through submit → store → read without backend changes — the validator's
 * image visitor only constrains the destination scheme; the alt-text capture group `[^\]]*` is
 * fully permissive, see `MarkdownAllowListValidator.kt` and `CommentAttachmentService.kt`.
 *
 * Wire format:
 *   `![filename](attachment:<uuid>)`     — legacy / no explicit size, renders at base CSS
 *   `![filename|s](attachment:<uuid>)`   — small
 *   `![filename|m](attachment:<uuid>)`   — medium
 *   `![filename|l](attachment:<uuid>)`   — large
 *
 * Three layers consume the same parser so the displayed alt stays clean (size suffix stripped
 * before binding to the rendered `<img>` for screen readers) while the underlying node attrs and
 * the markdown body both carry it:
 *  - The Tiptap Image extension's `renderHTML` derives the size CSS class from the alt suffix.
 *  - The read-side `decorateImages` post-sanitization step appends size caps to the inline style.
 *  - The pill row's seed effect reads the body markdown to populate the initial pill size.
 */

export type InlineImageSize = 's' | 'm' | 'l';

// Conservative match — only a single trailing pipe followed by exactly one of s/m/l counts as a
// size marker. A filename like `report|2026.png` won't be confused for a size suffix because the
// final character of its raw alt is `g`, not `s|m|l`.
const SIZE_SUFFIX_REGEX = /\|([sml])$/;

/**
 * Splits a raw alt string into its display portion and the optional size marker.
 */
export function parseSizeFromAlt(
  alt: string | null | undefined,
): { displayAlt: string; size: InlineImageSize | null } {
  if (!alt) return { displayAlt: '', size: null };
  const match = SIZE_SUFFIX_REGEX.exec(alt);
  if (!match || match.index === undefined) return { displayAlt: alt, size: null };
  return {
    displayAlt: alt.slice(0, match.index),
    size: match[1] as InlineImageSize,
  };
}

/**
 * Replaces (or adds) the size suffix on an alt string. Any pre-existing suffix is stripped first
 * so the result has at most one marker. Pass through {@link parseSizeFromAlt} for normalisation
 * before re-appending.
 */
export function encodeSizeIntoAlt(rawAlt: string, size: InlineImageSize): string {
  const { displayAlt } = parseSizeFromAlt(rawAlt);
  return `${displayAlt}|${size}`;
}

/**
 * Maps each inline image's `filestoreFileId` to its parsed size, scanning the body markdown for
 * `![alt|size](attachment:<uuid>)` tokens. Tokens with no recognisable size suffix map to `null`
 * (legacy). Returns an empty map when the body carries no `attachment:` references at all.
 */
export function extractInlineImageSizes(
  markdown: string,
): ReadonlyMap<string, InlineImageSize | null> {
  const out = new Map<string, InlineImageSize | null>();
  if (!markdown.includes('attachment:')) return out;
  const re =
    /!\[([^\]]*)]\(attachment:([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\)/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(markdown)) !== null) {
    const uuid = match[2]?.toLowerCase();
    if (!uuid) continue;
    const { size } = parseSizeFromAlt(match[1]);
    out.set(uuid, size);
  }
  return out;
}
